import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the plugin
gsap.registerPlugin(ScrollTrigger);

const CommonGridSection = ({
  subHeader, // e.g., "Our Benefits"
  mainHeader, // e.g., "Benefits of Using..."
  items = [], // Array of objects { title, desc, icon }
  backgroundColor = "bg-[#1C233A]",
  cardColor = "bg-[#B7DB83]",
  textColor = "text-white",
  cardTextColor = "text-[#1C233A]",
  gridCol = "grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4",
}) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content")
      );

      // --- 1. Scrubbed Text Reveal Animation ---
      textElements.forEach((element, index) => {
        // Set Initial State
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest("div"), // Trigger based on the parent container
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });

        // Target State
        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.05, // Slight stagger for natural feel
        });
      });

      // --- 2. Scrubbed Divider Line Animation ---
      const lineElement = sectionRef.current.querySelector(
        ".about-divider-line"
      );

      if (lineElement) {
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });

        gsap.to(lineElement, {
          scaleX: 1,
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: lineElement.closest(".about-subtitle"), // Matches the wrapper class added below
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <div ref={sectionRef} className={`w-full ${backgroundColor}`}>
      <div className="max-w-[1612px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-start items-center w-full py-[49px] sm:py-[73px] lg:py-[98px]">
          <div className="flex justify-center items-center w-full max-w-[806px] sm:max-w-[1612px] lg:max-w-[1612px]">
            <div className="flex flex-col gap-[12px] sm:gap-[18px] lg:gap-[24px] justify-start items-start w-full">
              {/* Section Header (Added 'about-subtitle' class for GSAP trigger) */}
              <div className="about-subtitle flex justify-start items-center w-full">
                <p
                  className={`animated-text-content text-[12px] sm:text-[18px] lg:text-[24px] font-normal leading-[15px] sm:leading-[23px] lg:leading-[31px] text-left ${textColor} w-auto font-[Sora]`}
                >
                  {subHeader}
                </p>
                {/* Divider Line (Added 'about-divider-line' class) */}
                <div
                  className={`about-divider-line w-[100px] sm:w-[150px] lg:w-[200px] h-[1px] sm:h-[1.5px] lg:h-[2px] ${
                    textColor === "text-white" ? "bg-white" : "bg-black"
                  } self-end ml-[15px] sm:ml-[22px] lg:ml-[30px] mb-[7px] sm:mb-[10px] lg:mb-[14px]`}
                ></div>
              </div>

              {/* Main Heading */}
              <h2
                className={`animated-text-content text-[30px] sm:text-[40px] md:text-[45px] 2xl:text-[60px] font-bold leading-[38px] sm:leading-[57px] lg:leading-[76px] text-left ${textColor} w-auto font-[Sora]`}
              >
                {mainHeader}
              </h2>

              {/* Grid Items */}
              <div
                className={` ${gridCol} gap-[11px] sm:gap-[16px] lg:gap-[22px] w-full mr-[2px] sm:mr-[3px] lg:mr-[4px]`}
              >
                {items.map((item, index) => (
                  <div
                    key={index}
                    className={`flex flex-col gap-[8px] sm:gap-[12px] lg:gap-[16px] justify-start items-start w-full h-auto ${cardColor} rounded-[15px] sm:rounded-[22px] lg:rounded-[30px] p-[13px] sm:p-[19px] lg:p-[26px]`}
                  >
                    {/* Icon Button */}
                    <button
                      className={`w-[30px] sm:w-[45px] lg:w-[60px] h-[30px] sm:h-[45px] lg:h-[60px] ${backgroundColor} rounded-[15px] sm:rounded-[22px] lg:rounded-[30px] p-[7px] sm:p-[10px] lg:p-[14px]`}
                    >
                      <img
                        src={item.icon || "/images/img_group_1.svg"} // Default or passed icon
                        alt={item.title}
                        className="w-full h-full"
                      />
                    </button>

                    {/* Card Title */}
                    <h3
                      className={`animated-text-content text-[15px] sm:text-[22px] lg:text-[30px] font-semibold leading-[22px] sm:leading-[33px] lg:leading-[45px] text-left ${cardTextColor} w-auto`}
                    >
                      {item.title}
                    </h3>

                    {/* Card Description */}
                    <p
                      className={`animated-text-content text-[12px] sm:text-[18px] lg:text-[24px] font-normal leading-[18px] sm:leading-[27px] lg:leading-[36px] text-left ${cardTextColor} w-full`}
                    >
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonGridSection;
